import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import { boolean, check, fallback, object, pipe, string, transform } from "valibot";
import { dataName, label, rememberInput, required } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { useFormServices } from "../../services/formServices";
import { isFilledInFilter } from "../../pages/customer/form/filter";
import FormatDate, { DEFAULT_DATE_FORMAT } from "../../components/formatting/FormatDate";
import { InputPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { Toggle } from "../../components/toggle/Toggle";
import { keyFromSelector } from "i18next";
import { isValidDate } from "../../utils/dateUtil";

const Schema = object({
  label_text: label(),
  data_name: dataName(),
  now_as_default: fallback(boolean(), false),
  required: required(),
  remember_input: rememberInput(),
});

const ValueSchema = pipe(
  string(),
  transform((value) => new Date(value)),
  check(isValidDate),
);

export type DateFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const DateField: Field<DateFieldProperties, DefaultDtoType, ValueType> = {
  type: "date",
  category: "dateTime",
  widget: "com.moreapps:date:1",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    now_as_default: false,
    required: false,
    remember_input: false,
  }),
  ruleBehaviour: {
    conditions: { variant: "text", keys: ["hasValue"] },
    actions: { variant: "date", setValue: { fieldTypes: ["date"], variableTypes: ["date"] } },
  },
  filter: {
    type: "dateString",
    definition: [
      { operator: "is", inputType: "date" },
      { operator: "isBefore", inputType: "date" },
      { operator: "isAfter", inputType: "date" },
      isFilledInFilter,
    ],
  },
  stringValue: (value) => value.toString(),
  renderValue: (value) => <FormatDate value={value} dateOnly />,
  icon: "calendar",
  titleKey: keyFromSelector(($) => $.DATE_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.DATE_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    now_as_default: keyFromSelector(($) => $.FIELD_PROPERTY_NOW_AS_DEFAULT),
    required: keyFromSelector(($) => $.FIELD_PROPERTY_REQUIRED),
    remember_input: keyFromSelector(($) => $.FIELD_PROPERTY_REMEMBER_INPUT),
  },
  PreviewContent: (props) => (
    <PreviewContentLabel uid={props.uid} label={props.properties.label_text} required={props.properties.required}>
      <InputPreview
        placeholder="dd/mm/yyyy"
        value={props.properties.now_as_default ? DEFAULT_DATE_FORMAT.format(new Date()) : undefined}
      />
    </PreviewContentLabel>
  ),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService } = useFormServices();

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />

          <Toggle
            block
            label={t(props.propertyKeys.now_as_default)}
            checked={props.data.properties.now_as_default}
            setChecked={(value) => props.setValue("now_as_default", value)}
            description={t(($) => $.FIELD_PROPERTY_NOW_AS_DEFAULT_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <Toggle
            block
            label={t(($) => $.REQUIRED_SWITCH)}
            checked={props.data.properties.required}
            setChecked={(checked) => props.setValue("required", checked)}
            description={t(($) => $.FIELD_PROPERTY_REQUIRED_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(($) => $.REMEMBER_INPUT_SWITCH)}
            checked={props.data.properties.remember_input}
            setChecked={(checked) => props.setValue("remember_input", checked)}
            description={t(($) => $.FIELD_PROPERTY_REMEMBER_INPUT_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(DateField);
